import {
   faAddressCard,
   faBookmark,
   faCircleDollarToSlot,
   faGaugeHigh,
   faGear,
   faHeart,
   faUser,
} from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import classNames from 'classnames'
import { ReactNode, useContext } from 'react'
import { NavLink } from 'react-router-dom'
import { AppContext } from './Layout'
import { useIntl } from 'react-intl'
import { useAppSelector } from '../store/hooks'
import { isAuthorized } from '../utils/auth'

type Props = {}

export const Logo = () => (
   <>
      <div className="fa-stack fa-sm">
         <FontAwesomeIcon icon={faBookmark} className="fa-stack-2x" />
         <FontAwesomeIcon icon={faHeart} className="fa-stack-1x text-amber-500 !h-2.5" />
      </div>
      <span className="text-xl font-bold ml-1">Loyalty Center</span>
   </>
)

const MenuItem = ({
   children,
   href,
   icon,
   collapsed,
}: {
   children?: ReactNode
   href: string
   icon?: React.ReactNode
   collapsed?: boolean
}) => {
   const label = typeof children === 'string' ? children : undefined

   return (
      <NavLink
         to={href}
         title={collapsed ? label : undefined}
         className={({ isActive }) =>
            classNames('block cursor-pointer py-4 transition text-zinc-600 text-[16px]', {
               'bg-zinc-600/10': isActive,
               'hover:bg-zinc-600/5': !isActive,
               'px-6': !icon,
               'relative px-14': icon,
               'lg:flex lg:items-center lg:justify-center lg:px-0': collapsed,
            })
         }
      >
         {icon && (
            <div
               className={classNames('flex items-center text-amber-500', {
                  'absolute top-5 left-6': icon,
                  'lg:static': collapsed,
               })}
            >
               {icon}
            </div>
         )}
         <span className={classNames({ 'lg:hidden': collapsed })}>{children}</span>
      </NavLink>
   )
}

const SideMenu = (props: Props) => {
   const intl = useIntl()
   const { isMenuOpen, isSidebarCollapsed, toggleMenu } = useContext(AppContext)
   const base = useAppSelector((state) => state.base)
   const profile = useAppSelector((state) => state.profile)
   const hasPermission = profile.data && isAuthorized({ roles: profile?.data?.permissions, target: 'LTY_CFG_STM' })

   return (
      <div
         className={classNames('fixed inset-0 pointer-events-none lg:block', {
            hidden: !isMenuOpen,
            'z-30': isMenuOpen,
         })}
      >
         {isMenuOpen && (
            <div
               className="fixed lg:hidden inset-0 bg-black/40 backdrop-blur-sm pointer-events-auto"
               onClick={toggleMenu}
            ></div>
         )}
         <div
            className={classNames(
               'fixed left-0 bottom-0 top-0 lg:top-16 flex flex-col bg-white z-30 pointer-events-auto overflow-y-auto',
               isSidebarCollapsed ? 'w-[17rem] lg:w-16 lg:overflow-hidden' : 'w-[17rem]',
            )}
         >
            <div className="bg-amber-500 text-white h-16 flex items-center lg:hidden">
               <Logo />
            </div>
            <div
               className={classNames(
                  'flex-1 min-h-0 lg:border-r lg:border-zinc-900/10 lg:pt-4 bg-white overflow-y-auto',
                  { 'lg:overflow-hidden': isSidebarCollapsed },
               )}
            >
               <ul>
                  <li>
                     <MenuItem
                        href={`${base.account?.code}/dashboard`}
                        icon={<FontAwesomeIcon icon={faGaugeHigh} />}
                        collapsed={isSidebarCollapsed}
                     >
                        {intl.formatMessage({ id: 'titles.dashboard' })}
                     </MenuItem>
                  </li>
                  <li>
                     <MenuItem
                        href={`${base.account?.code}/programs`}
                        icon={<FontAwesomeIcon icon={faAddressCard} />}
                        collapsed={isSidebarCollapsed}
                     >
                        {intl.formatMessage({ id: 'titles.programs' })}
                     </MenuItem>
                  </li>
                  <li>
                     <MenuItem
                        href={`${base.account?.code}/partners`}
                        icon={<FontAwesomeIcon icon={faUser} />}
                        collapsed={isSidebarCollapsed}
                     >
                        {intl.formatMessage({ id: 'titles.partners' })}
                     </MenuItem>
                  </li>
                  <li>
                     <MenuItem
                        href={`${base.account?.code}/operations`}
                        icon={<FontAwesomeIcon icon={faCircleDollarToSlot} />}
                        collapsed={isSidebarCollapsed}
                     >
                        {intl.formatMessage({ id: 'titles.operations' })}
                     </MenuItem>
                  </li>
                  <li className={`${hasPermission ? '' : 'hidden'}`}>
                     <MenuItem
                        href={`${base.account?.code}/configuration`}
                        icon={<FontAwesomeIcon icon={faGear} />}
                        collapsed={isSidebarCollapsed}
                     >
                        {intl.formatMessage({ id: 'titles.settings' })}
                     </MenuItem>
                  </li>
               </ul>
            </div>
         </div>
      </div>
   )
}

export default SideMenu
